import { Reducer, useReducer, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ProgressSteps, MainContentPaddingTypes, NewModal, Section, WaitAnimation, NewLfSectionModalLvl1, NewLfSectionModalLvl2, NewLfSectionModalLvl3, NewButton } from "..";
import { colors, glyphs, useMediaQuery } from "../../Styles";
import { Graph_QL, useOldSection, useTerminalTypes } from "../../Services";
import { StoreAppDispatch, useAppSelector } from "../../Store";
import { DirectionOptions, NumberingOptions, PatternOptions, Pen, StartCornerOptions } from "../../Definitions/newSectionModalInterfaces";
import { SystemTypes } from "../../Definitions";
import NewEsfWfSectionModalLvl1 from "../EsfWalkThrough/Modals/newEsfWfSectionModalLvl1";
import NewEsfWfSectionModalLvl2 from "../EsfWalkThrough/Modals/newEsfWfSectionModalLvl2";
import NewEsfWfSectionModalLvl3 from "../EsfWalkThrough/Modals/newEsfWfSectionModalLvl3";
import { CreateTerminalLink } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/createLfSectionCall";
import { useLazyQuery } from "@apollo/client";
import { VERIFY_SECTION_CREATE, VerifySectionCreateResponse } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/verifySectionCreateCall";
import { IModalContext, ModalContext, ModalDispatcherT, ModalReducer } from "../LocationFeeder/_LocationFeeder/newSectionModalContext";
import { esfExtractTerminalLinks, lfExtractTerminalLinks, transformData } from "../LocationFeeder/_LocationFeeder/newSectionModalHelpers";
import { DevicePortsUsed, dispatchTerminalConflict } from "../LocationFeeder/_LocationFeeder/dispatchTerminalConflict";
import { useDispatch } from "react-redux";
import { showSuccessWithTimeout } from "../../Store/Thunks/successWithTimeout";

interface NewSectionModal_Props {
  closeModal: () => void,
  systemId: number;
  systemType: SystemTypes
}

export default function NewSectionModal({ closeModal, systemId, systemType }: NewSectionModal_Props) {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  const uiDispatch = useDispatch<StoreAppDispatch>();

  // #region | GraphQl 
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { domain, connection } = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm })
    let connection = Graph_QL.create({ domain: farm })
    return { domain, connection }
  }, [farm]);

  const { createSectionLocations } = useOldSection(connection);
  const { data: terminalTypesState, call: getTerminalTypes } = useTerminalTypes(connection);
  // #endregion

  // #region | States
  const [loading, setLoading] = useState(false);
  const [verifyNewTerminals,] = useLazyQuery<VerifySectionCreateResponse>(VERIFY_SECTION_CREATE, { fetchPolicy: "no-cache" })

  const [_state, dispatch] = useReducer<Reducer<IModalContext, ModalDispatcherT>>(ModalReducer, {
    farm: farm,
    domain: domain,
    connection: connection,
    currentSectionLevel: 1,
    section: {
      name: "",
      contents: [],
      feeder: 1,
      lanController: "",
      columns: systemType === SystemTypes.freedaLf ? 2 : systemType === SystemTypes.freedaEsf_WalkThrough ? 1 : 2,
      rows: 5,
      startNumber: 101,
      numbering: NumberingOptions.manual,
      startCorner: StartCornerOptions.topLeft,
      direction: DirectionOptions.horizontal,
      pattern: PatternOptions.z
    } as Section,
    systemType: systemType,
    error: { Elementid: 0, errorText: "" }
  } as IModalContext)
  // #endregion

  useEffect(() => {
    if (!terminalTypesState[domain]) {
      getTerminalTypes(undefined)
    }
  }, [domain, getTerminalTypes, terminalTypesState]);

  // Go Back Handler
  const goBack = () => {
    if (_state.currentSectionLevel > 1) dispatch({ currentSectionLevel: _state.currentSectionLevel - 1 })
  };

  // #region | Save Section
  const handleSaveSection = async () => {
    // Handle Timeout
    const handleTimeout = (elementId: number) => {
      setTimeout(() => {
        dispatch({ error: { Elementid: elementId, errorText: "" } });
      }, 2000)
    }

    // Lvl 1: Verification
    if (_state.currentSectionLevel === 1) {
      if (!_state.section.name) {
        return (
          dispatch({ currentSectionLevel: 1, error: { Elementid: 1, errorText: "Missing name" } }),
          handleTimeout(1)
        )
      }
      if (!_state.section.contents.length) {
        return (
          dispatch({ currentSectionLevel: 1, error: { Elementid: 2, errorText: "Missing pens" } }),
          handleTimeout(2)
        )
      }
      return dispatch({ currentSectionLevel: _state.currentSectionLevel + 1 })
    }

    // New Section Data
    const newSectionData = transformData(_state.section.contents, _state.section, _state.systemType)

    // Lvl 2: Verification
    if (_state.currentSectionLevel === 2) {
      if (_state.section.contents.some((pen) => {
        if ("io" in pen) {
          return !pen.io
        }
        if ("terminals" in pen) {
          return !Object.values(pen.terminals).some(terminal => terminal.io !== undefined && terminal.io !== null)
        }
        return false;
      })) {
        return (
          dispatch({ currentSectionLevel: 2, error: { Elementid: 3, errorText: "Missing I/O Board" } }),
          handleTimeout(3)
        )
      }
      if (_state.section.contents.some((pen) => {
        if ("feederOutput" in pen || "penId" in pen) {
          return !pen.feederOutput
        }
        if ("terminals" in pen) {
          return !Object.values(pen.terminals).some(terminal => terminal.__terminalType === "feederOutput" && terminal.port !== undefined && terminal.port !== null)
        }
        return false;
      })) {
        return (
          dispatch({ currentSectionLevel: 2, error: { Elementid: 4, errorText: "Missing feeder output" } }),
          handleTimeout(4)
        )
      }
      if (_state.systemType === SystemTypes.freedaLf && _state.section.water && _state.section.contents.some((pen) => (pen as Pen).waterOutput === undefined)) {
        return (
          dispatch({ currentSectionLevel: 2, error: { Elementid: 5, errorText: "Missing water output" } }),
          handleTimeout(5)
        )
      }
      if (_state.systemType === SystemTypes.freedaLf && _state.section.activator && _state.section.contents.some((pen) => (pen as Pen).activatorInput === undefined)) {
        return (
          dispatch({ currentSectionLevel: 2, error: { Elementid: 6, errorText: "Missing activator input" } }),
          handleTimeout(6)
        )
      }

      setLoading(true);

      const verifyCreate = await verifyNewTerminals({ variables: { domain: farm, section: newSectionData } })

      const devicePortsUsed = verifyCreate.data?.section.verifyCreate.existingTerminals.reduce((accumulator, terminal) => {
        if (accumulator[terminal.device_id] === undefined) {
          accumulator[terminal.device_id] = [];
        }
        accumulator[terminal.device_id].push(terminal.port);
        return accumulator
      }, {} as DevicePortsUsed)

      setLoading(false);

      if (dispatchTerminalConflict(dispatch, _state.section.contents as Pen[], devicePortsUsed)) {
        return (
          dispatch({ currentSectionLevel: 2, error: { Elementid: 0, errorText: "Duplicate ports" } }),
          handleTimeout(0)
        )
      }

      return dispatch({ currentSectionLevel: _state.currentSectionLevel + 1 })
    }

    // New Terminal Data
    let terminalLinks: CreateTerminalLink[] | undefined = undefined;

    if (systemType === SystemTypes.freedaLf) { terminalLinks = lfExtractTerminalLinks(_state.section) }


    if (systemType === SystemTypes.freedaEsf_WalkThrough) {
      terminalLinks = esfExtractTerminalLinks(_state.section);
    }

    setLoading(true)
    // Lvl 3: Save Sata
    const response = await createSectionLocations(connection.connection.domain, _state.section.name, Number(_state.section.columns), systemId, newSectionData, terminalLinks ? terminalLinks : undefined)
    if (!response.success) {
      dispatch({ currentSectionLevel: 3, error: { Elementid: 3, errorText: response.message } });
      handleTimeout(3);
      setLoading(false);
      return
    }
    uiDispatch(showSuccessWithTimeout(`${_state.section.name} was created`));
    setLoading(false);
    closeModal();
  }
  // #endregion

  // #region | Content Router
  let contentRes: JSX.Element = <></>;

  switch (_state.currentSectionLevel) {
    case 1:
      switch (_state.systemType) {
        case SystemTypes.freedaLf:
          contentRes = <NewLfSectionModalLvl1 />;
          break;
        case SystemTypes.freedaEsf_WalkThrough:
          contentRes = <NewEsfWfSectionModalLvl1 />;
          break;
        default:
          break;
      }
      break;
    case 2:
      switch (_state.systemType) {
        case SystemTypes.freedaLf:
          contentRes = <NewLfSectionModalLvl2 />;
          break;
        case SystemTypes.freedaEsf_WalkThrough:
          contentRes = <NewEsfWfSectionModalLvl2 />;
          break;
        default:
          break;
      }
      break;
    case 3:
      switch (_state.systemType) {
        case SystemTypes.freedaLf:
          contentRes = <NewLfSectionModalLvl3 />;
          break;
        case SystemTypes.freedaEsf_WalkThrough:
          contentRes = <NewEsfWfSectionModalLvl3 />;
          break;
        default:
          break;
      }
      break;
    default:
      break;
  }
  // #endregion

  return (
    <ModalContext.Provider value={{ state: _state, dispatch }}>
      <NewModal
        modalState={true}
        overrides={{
          mainContentPadding: MainContentPaddingTypes.secondary,
          headerContentHeight: "65px",
          secondaryContentScroll: true
        }}
        predefinedButtons={{
          onClose: closeModal,
          onGoBack: _state.currentSectionLevel > 1 ? goBack : undefined
        }}
        header={
          systemType === SystemTypes.freedaLf ? t("locationFeederPages.sections.newSection", { defaultValue: "new section" })
            : systemType === SystemTypes.freedaEsf_WalkThrough ? t("esfWalkThroughPages.newSection.newPen", { defaultValue: "new pen" })
              : ""
        }
        headerContent={
          <ProgressSteps
            titles={[t("devices.layout", { defaultValue: "layout" }), t("devices.ioBoards", { defaultValue: "i/o boards" }), t("buttons.apply", { defaultValue: "apply" })]}
            icons={[glyphs.Animal({}), glyphs.Animal({}), glyphs.Animal({})]}
            opacityValues={_state.currentSectionLevel === 1 ? [1, 0.5, 0.5] : _state.currentSectionLevel === 2 ? [0.5, 1, 0.5] : [0.5, 0.5, 1]}
          />
        }
        mainContent={
          <div style={{ minWidth: mobileXs ? "290px" : "615px", height: "100%", overflow: "hidden" }}>
            {contentRes}
          </div>
        }
        footerContent={
          <NewButton
            content={_state.currentSectionLevel < 3 ? t("buttons.next", { defaultValue: "next" }) : t("buttons.create", { defaultValue: "create" })}
            onClick={handleSaveSection}
          />
        }
        footerMessage={_state.error.errorText !== "" ? _state.error.errorText : loading ? <WaitAnimation color={colors.greyLightTertiary} /> : undefined}
      />
    </ModalContext.Provider>
  );
}

